import { getMetadataFilterValidationError } from "@/lib/metadataFilters"
import type { MetricValueFilter } from "@/lib/metricValueFilters"
import type {
  RunsListFiltersInput,
  StatusFilters,
} from "@/types_api/backend/transport/runs_keyset_request"
import type { RunsListFiltersInput as InsightsFiltersInput } from "@/types_api/backend/transport/runs_page_insights_request"
import type { MetadataFilter } from "@/types/MetadataFilter"

interface RunsTransportFilters {
  searchQuery?: string
  agentFilters?: string[]
  statusFilters?: string[]
  templateFilters?: string[]
  testSetFilters?: string[]
  testCaseFilters?: string[]
  personaFilters?: string[]
  metricFilters?: string[]
  metricValueFilters?: MetricValueFilter[]
  tagFilters?: string[]
  mutationFilters?: string[]
  scheduledRunId?: string
  metadataFilters?: MetadataFilter[]
  scheduledJobFilters?: string[]
  dateRange?: {
    start?: Date
    end?: Date
  }
}

export const MAX_RUN_STATUS_FILTERS = 20

export function getRunsFilterValidationError(filters: RunsTransportFilters) {
  return getMetadataFilterValidationError(filters.metadataFilters)
}

function normalizeStatusFilters(filters?: string[]): StatusFilters | undefined {
  if (!filters) {
    return undefined
  }

  // Generated transport types encode maxItems as tuples; runtime validation
  // still accepts ordinary arrays, so keep this cast isolated at the boundary.
  return filters.slice(0, MAX_RUN_STATUS_FILTERS) as StatusFilters
}

export function buildRunsTransportFilters(
  filters: RunsTransportFilters,
): RunsListFiltersInput | null {
  if (getRunsFilterValidationError(filters)) {
    return null
  }
  return {
    search_query: filters.searchQuery,
    agent_filters: filters.agentFilters,
    status_filters: normalizeStatusFilters(filters.statusFilters),
    template_filters: filters.templateFilters,
    test_set_filters: filters.testSetFilters,
    test_case_filters: filters.testCaseFilters,
    persona_filters: filters.personaFilters,
    metric_filters: filters.metricFilters,
    metric_value_filters: filters.metricValueFilters?.map((filter) => ({
      metric_name: filter.metricName,
      operator: filter.operator,
      value: filter.value,
      range_max: filter.rangeMax ?? null,
      value_type: filter.valueType,
    })),
    tag_filters: filters.tagFilters,
    mutation_filters: filters.mutationFilters,
    scheduled_run_id: filters.scheduledRunId,
    scheduled_job_filters: filters.scheduledJobFilters,
    metadata_filters: filters.metadataFilters?.map(
      ({ key, value, matchType, values }) => ({
        key,
        value,
        match_type: matchType,
        ...(values ? { values } : {}),
      }),
    ),
    date_range: filters.dateRange
      ? {
          start: filters.dateRange.start?.toISOString() ?? null,
          end: filters.dateRange.end?.toISOString() ?? null,
        }
      : undefined,
  }
}

export function buildRunsInsightsTransportFilters(
  filters: RunsTransportFilters,
): InsightsFiltersInput | null {
  return buildRunsTransportFilters(filters)
}
